import { FC, KeyboardEvent, useEffect, useState } from "react";

interface TableViewerProps {
    csvData: string;
    maxRows?: number;
    onDataChange?: (newData: string) => void;
    extraRows?: number;
    extraCols?: number;
}

const TableViewer: FC<TableViewerProps> = ({
    csvData,
    maxRows = 1000,
    onDataChange,
    extraRows = 20,
    extraCols = 5
}) => {
    const [headers, setHeaders] = useState<string[]>([]);
    const [dataRows, setDataRows] = useState<string[][]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [isPreview, setIsPreview] = useState(false);
    const [editCell, setEditCell] = useState<{ row: number, col: number } | null>(null);
    const [selectedCell, setSelectedCell] = useState<{ row: number, col: number } | null>(null);
    const [editValue, setEditValue] = useState("");
    const [hoveredColumn, setHoveredColumn] = useState<number | null>(null);

    // Function to generate Excel-style column headers (A, B, C, ..., Z, AA, AB, etc.)
    const getColumnLabel = (index: number): string => {
        let label = '';
        while (index >= 0) {
            label = String.fromCharCode(65 + (index % 26)) + label;
            index = Math.floor(index / 26) - 1;
        }
        return label;
    };

    useEffect(() => {
        const allRows = csvData.split("\n").filter((row) => row.trim() !== "");
        const totalRowCount = allRows.length - 1; // Subtract header row
        setTotalRows(totalRowCount);

        // Set headers
        const headerRow = allRows[0]?.split(",") || [];
        setHeaders(headerRow);

        // Handle preview mode
        if (totalRowCount > maxRows) {
            setIsPreview(true);
            // Take first maxRows rows plus header
            const previewRows = allRows.slice(0, maxRows + 1);
            const previewData = previewRows.slice(1).map(row => row.split(","));
            setDataRows(previewData);
        } else {
            setIsPreview(false);
            const data = allRows.slice(1).map(row => row.split(","));
            setDataRows(data);
        }
    }, [csvData, maxRows]);

    const handleCellClick = (rowIndex: number, colIndex: number, value: string) => {
        setSelectedCell({ row: rowIndex, col: colIndex });
        // Only allow editing of actual data cells, not the extra rows/columns
        if (rowIndex < dataRows.length && colIndex < headers.length) {
            setEditCell({ row: rowIndex, col: colIndex });
            setEditValue(value);
        }
    };

    const handleCellBlur = () => {
        if (editCell) {
            const newData = [...dataRows];
            newData[editCell.row][editCell.col] = editValue;
            setDataRows(newData);
            setEditCell(null);
            // Save changes immediately
            if (onDataChange) {
                const csvString = [
                    headers.join(","),
                    ...newData.map(row => row.join(","))
                ].join("\n");
                onDataChange(csvString);
            }
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTableElement>) => {
        if (editCell) {
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                handleCellBlur();
                // Move to next cell
                if (e.key === 'Enter') {
                    const nextRow = editCell.row + 1;
                    if (nextRow < dataRows.length) {
                        handleCellClick(nextRow, editCell.col, dataRows[nextRow][editCell.col] || "");
                    }
                } else if (e.key === 'Tab') {
                    const nextCol = editCell.col + (e.shiftKey ? -1 : 1);
                    if (nextCol >= 0 && nextCol < headers.length) {
                        handleCellClick(editCell.row, nextCol, dataRows[editCell.row][nextCol] || "");
                    }
                }
            }
        } else if (selectedCell) {
            // Navigation when not editing
            let newRow = selectedCell.row;
            let newCol = selectedCell.col;

            switch (e.key) {
                case 'ArrowUp':
                    newRow = Math.max(0, selectedCell.row - 1);
                    break;
                case 'ArrowDown':
                    newRow = Math.min(totalDisplayRows - 1, selectedCell.row + 1);
                    break;
                case 'ArrowLeft':
                    newCol = Math.max(0, selectedCell.col - 1);
                    break;
                case 'ArrowRight':
                    newCol = Math.min(totalDisplayCols - 1, selectedCell.col + 1);
                    break;
                case 'Enter':
                    if (newRow < dataRows.length && newCol < headers.length) {
                        handleCellClick(newRow, newCol, dataRows[newRow]?.[newCol] || "");
                    }
                    break;
                default:
                    return;
            }

            e.preventDefault();
            setSelectedCell({ row: newRow, col: newCol });
        }
    };

    // Calculate total number of rows and columns to display
    const totalDisplayRows = Math.max(dataRows.length + extraRows, 100);
    const totalDisplayCols = Math.max(headers.length + extraCols, 26);

    return (
        <div className="tableviewer-container transition-all duration-300 ease-in-out transform">
            {isPreview && (
                <div className="tableviewer-preview-notice transition-all duration-300 ease-in-out transform translate-x-0">
                    Showing preview of {maxRows} rows out of {totalRows} total rows
                </div>
            )}
            <div className="tableviewer-grid-container transition-all duration-300 ease-in-out transform translate-x-0" style={{ overflowX: 'auto', maxWidth: '100%' }}>
                <table
                    className="tableviewer-table"
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    style={{ minWidth: `${totalDisplayCols * 120}px` }}
                >
                    <thead className="tableviewer-thead">
                        <tr>
                            <th className="tableviewer-row-header">
                                <span className="text-gray-400">#</span>
                            </th>
                            {Array(totalDisplayCols).fill(0).map((_, index) => (
                                <th
                                    key={index}
                                    className={`tableviewer-th ${hoveredColumn === index ? 'tableviewer-th-hover' : ''} ${index % 2 === 0 ? 'tableviewer-col-even' : ''}`}
                                    onMouseEnter={() => setHoveredColumn(index)}
                                    onMouseLeave={() => setHoveredColumn(null)}
                                >
                                    <span className="text-gray-400">{getColumnLabel(index)}</span>
                                </th>
                            ))}
                        </tr>
                        <tr>
                            <th className="tableviewer-row-header">
                                <span className="text-gray-400">0</span>
                            </th>
                            {Array(totalDisplayCols).fill(0).map((_, index) => (
                                <th
                                    key={index}
                                    className={`tableviewer-th ${hoveredColumn === index ? 'tableviewer-th-hover' : ''} ${index % 2 === 0 ? 'tableviewer-col-even' : ''}`}
                                    onMouseEnter={() => setHoveredColumn(index)}
                                    onMouseLeave={() => setHoveredColumn(null)}
                                >
                                    {index < headers.length ? headers[index] : ""}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {Array(totalDisplayRows).fill(0).map((_, rowIndex) => (
                            <tr key={rowIndex} className={rowIndex % 2 === 0 ? "tableviewer-row-even" : "tableviewer-row-odd"}>
                                <td className="tableviewer-row-header">
                                    <span className="text-gray-400">{rowIndex + 1}</span>
                                </td>
                                {Array(totalDisplayCols).fill(0).map((_, colIndex) => {
                                    const isRealCell = rowIndex < dataRows.length && colIndex < headers.length;
                                    const cellValue = isRealCell ? (dataRows[rowIndex]?.[colIndex] || "") : "";
                                    const isSelected = selectedCell?.row === rowIndex && selectedCell?.col === colIndex;
                                    const isColumnHovered = hoveredColumn === colIndex;

                                    return (
                                        <td
                                            key={colIndex}
                                            className={`tableviewer-td ${isSelected ? 'tableviewer-td-selected' : ''} 
                                                      ${isColumnHovered ? 'tableviewer-td-col-hover' : ''}
                                                      ${colIndex % 2 === 0 ? 'tableviewer-col-even' : ''}`}
                                            onClick={() => handleCellClick(rowIndex, colIndex, cellValue)}
                                            style={{
                                                cursor: isRealCell ? 'cell' : 'default',
                                                backgroundColor: !isRealCell ? 'rgb(250, 250, 250)' : undefined,
                                                color: !isRealCell ? 'rgb(163, 163, 163)' : undefined
                                            }}
                                        >
                                            {editCell?.row === rowIndex && editCell?.col === colIndex ? (
                                                <input
                                                    type="text"
                                                    value={editValue}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    onBlur={handleCellBlur}
                                                    onKeyDown={handleKeyDown}
                                                    className="tableviewer-input"
                                                    autoFocus
                                                />
                                            ) : (
                                                cellValue
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TableViewer;